import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, LogIn, UserPlus, Mail, Lock, User as UserIcon } from "lucide-react";
import { useAuth } from "../lib/auth";
import { formatError } from "../lib/api";

export default function AuthModal({ open, onClose, initialMode = "login" }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { setMode(initialMode); setErr(""); }, [initialMode, open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(email, password, username);
      }
      onClose();
      setEmail(""); setPassword(""); setUsername("");
    } catch (e) {
      setErr(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, y: 10 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 10 }}
          transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
          onClick={(e) => e.stopPropagation()}
          data-testid="auth-modal"
          className="glass-red-glow rounded-2xl w-full max-w-md p-8 relative"
        >
          <button
            onClick={onClose}
            data-testid="auth-modal-close"
            className="absolute top-4 right-4 h-8 w-8 rounded-full glass-dark flex items-center justify-center hover:text-red-400"
          >
            <X size={14} />
          </button>

          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono mb-1">
              {mode === "login" ? "Welcome back" : "Join the arena"}
            </div>
            <h2 className="font-display font-black text-3xl tracking-tight">
              {mode === "login" ? "Sign In" : "Create Account"}
            </h2>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-2 p-1 glass-dark rounded-full mb-6">
            <button
              type="button"
              onClick={() => setMode("login")}
              data-testid="auth-tab-login"
              className={`flex-1 py-2 rounded-full text-sm font-mono uppercase tracking-widest transition-all ${
                mode === "login" ? "bg-red-600 text-white shadow-[0_0_14px_rgba(230,0,0,0.5)]" : "text-zinc-400 hover:text-white"
              }`}
            >
              Sign In
            </button>
            <button
              type="button"
              onClick={() => setMode("register")}
              data-testid="auth-tab-register"
              className={`flex-1 py-2 rounded-full text-sm font-mono uppercase tracking-widest transition-all ${
                mode === "register" ? "bg-red-600 text-white shadow-[0_0_14px_rgba(230,0,0,0.5)]" : "text-zinc-400 hover:text-white"
              }`}
            >
              Register
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <Field icon={UserIcon} label="Username" testid="auth-username"
                value={username} onChange={setUsername} placeholder="spicy_gamer" />
            )}
            <Field icon={Mail} label="Email" testid="auth-email" type="email"
              value={email} onChange={setEmail} placeholder="you@example.com" />
            <Field icon={Lock} label="Password" testid="auth-password" type="password"
              value={password} onChange={setPassword} placeholder="••••••••" />

            {err && (
              <div data-testid="auth-error" className="text-sm text-red-400 bg-red-950/40 border border-red-500/30 rounded-lg p-3">
                {err}
              </div>
            )}
            <button
              type="submit" disabled={loading}
              data-testid="auth-submit"
              className="btn-liquid btn-liquid-lg w-full"
            >
              {mode === "login" ? <LogIn size={16} /> : <UserPlus size={16} />}
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-white/5 text-center text-xs text-zinc-500">
            {mode === "login" ? (
              <>
                Don't have an account?{" "}
                <button type="button" onClick={() => setMode("register")} className="text-red-400 font-semibold hover:text-red-300">
                  Create one
                </button>
              </>
            ) : (
              <>
                Already a member?{" "}
                <button type="button" onClick={() => setMode("login")} className="text-red-400 font-semibold hover:text-red-300">
                  Sign in
                </button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Field({ icon: Icon, label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
      <div className="mt-1 relative">
        <Icon size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input
          data-testid={testid} type={type} value={value} required
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-black/50 border border-white/10 rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:border-red-500 transition-all"
        />
      </div>
    </label>
  );
}
